import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "../components/AdminLayout";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { UserCog } from "lucide-react";

const VERTICAL_LABELS = {
  law_enforcement: "Law Enforcement", fire_service: "Fire Service", ems: "EMS",
  dispatch_psap: "Dispatch / PSAP", corrections: "Corrections", school_safety: "School Safety",
  military: "Military", emergency_medicine: "Emergency Medicine",
};

export default function AgencyRoster() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/agency/roster").then(r => setItems(r.data)).catch(() => {}); }, []);

  const isDemo = !!user?.is_demo;

  const impersonate = async (target) => {
    try {
      await api.post("/demo/impersonate", { user_id: target.user_id });
      await checkAuth?.();
      // Nudge the impersonated member view to highlight the "Start scenario"
      // CTA — the actual training moment is the whole point of View-as.
      try { localStorage.setItem("haruki_demo_run_first_scenario", "1"); }
      catch { /* localStorage unavailable — nudge simply won't fire */ }
      toast.success(`Viewing as ${target.name}`);
      nav("/app");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch view");
    }
  };

  return (
    <AdminLayout>
      <div className="haruki-fade-in">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-bold">Roster</h1>
            <p className="mt-2 text-sm text-muted-foreground">
              Members, vertical assignments and in-service dates.
            </p>
          </div>
          {isDemo && (
            <span data-testid="roster-demo-tag"
                  className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-xs font-medium text-amber-600">
              Demo workspace · synthetic data
            </span>
          )}
        </div>

        <Card className="mt-8">
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b border-border text-left text-xs uppercase tracking-widest text-muted-foreground">
                <tr>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Email</th>
                  <th className="px-4 py-3">Vertical</th>
                  <th className="px-4 py-3">In-service</th>
                  {isDemo && <th className="px-4 py-3 text-right">Demo view</th>}
                </tr>
              </thead>
              <tbody>
                {items.map(i => (
                  <tr key={i.user_id} data-testid={`roster-row-${i.user_id}`} className="border-b border-border last:border-0">
                    <td className="px-4 py-3 font-medium">{i.name}</td>
                    <td className="px-4 py-3 text-muted-foreground">{i.email}</td>
                    <td className="px-4 py-3">{VERTICAL_LABELS[i.vertical] || i.vertical}</td>
                    <td className="px-4 py-3 text-muted-foreground">{i.in_service_date?.slice(0, 10) || "—"}</td>
                    {isDemo && (
                      <td className="px-4 py-3 text-right">
                        <Button variant="ghost" size="sm" data-testid={`roster-impersonate-${i.user_id}`}
                                onClick={() => impersonate(i)}>
                          <UserCog className="mr-1 h-3 w-3" /> View as
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={isDemo ? 5 : 4} className="px-4 py-6 text-center text-sm text-muted-foreground">No members yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    </AdminLayout>
  );
}
